import { extent, scale } from 'd3'
import { useMemo } from 'react'

export type Point = [number, number]

export const useChart = (points: Point[], width: number, height: number) => {
  const chartStat = useMemo(() => {
    const breakdown = points.reduce((acc, item) => {
      const [x, y] = item
      if (!acc.yArray.has(y)) {
        acc.yArray.add(y)
      }

      if (!acc.xArray.has(x)) {
        acc.xArray.add(x)
      }

      let group = acc.groupedByX[x]
      if (!group) {
        group = acc.groupedByX[x] = []
      }
      group.push(y)

      return acc
    }, {
      xArray: new Set<number>(),
      yArray: new Set<number>(),
      groupedByX: {} as Record<number, number[]>,
    })

    const xArray = Array.from(breakdown.xArray)
    const yArray = Array.from(breakdown.yArray)

    return {
      points,
      xArray,
      yArray,
      groupedByX: breakdown.groupedByX,
      xRange: extent(xArray),
      yRange: extent(yArray),
    }

  }, [points])

  return useMemo(() => {
    let [min, max] = chartStat.yRange

    max = max
    min = Math.min(0, min)
    const yRange = [min, max]

    return {
      ...chartStat,
      yRange,
      scaleX: scale.linear()
        .domain(chartStat.xRange)
        .range([0, width]),

      scaleY: scale.linear()
        .domain(yRange)
        .range([height, 0]),
    }

  }, [chartStat, height, width])
}

export type UseChartContext = ReturnType<typeof useChart>
